import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  OutlinedInput,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { getProductDetail } from "../../../../../http/api/ProductDetailAPI";
import { getDisplaySize } from "../../../../../utils/Items";
import AddProductSidebarHeader from "./AddProductSidebarHeader";
import AddProductSidebarRow from "./AddProductSidebarRow";

const TABS_LABEL = { 1: "蔬菜", 2: "果物", 3: "スポット" };
const PAGE_SIZE = 30;

const defaultPredictAdded = (product, addedProduct) =>
  product.productId === addedProduct.productId &&
  product.productDetailId === addedProduct.productDetailId;

const AddProductSidebar = ({
  open,
  activeMode,
  tabPosition,
  addedProductList,
  predictAddedProduct,
  onAddProduct,
  onAddProductMaster,
  onAddProductDetailMaster,
  onDragBegin,
  onDragCancel,
  onClose,
  refreshTrigger,
  scrollToProductId,
  onScrollComplete,
}) => {
  const [productInfoList, setProductInfoList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollRef = useRef(null);

  const predictFn = predictAddedProduct || defaultPredictAdded;
  const activePart = tabPosition ?? 1;

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await getProductDetail({});
      const list = res.listProductDetailInfo.map((data) => ({
        ...data,
        displaySize: getDisplaySize(
          data.rank,
          data.productClass,
          data.weight,
          data.weightUnit,
          data.packing,
          data.quantity
        ),
      }));
      setProductInfoList(list);
      return list;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when refreshTrigger changes (after dialog closes)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Auto-scroll to newly added product after data loads
  useEffect(() => {
    if (!scrollToProductId || !scrollRef.current || productInfoList.length === 0) return;

    // Find the index of the product in the filtered list
    const targetIndex = productInfoList.findIndex(
      (p) => p.productId === scrollToProductId
    );
    if (targetIndex < 0) return;

    // Ensure enough items are visible for scroll
    if (targetIndex >= visibleCount) {
      setVisibleCount(targetIndex + PAGE_SIZE);
    }

    // Scroll to the row after a short delay (to let DOM update)
    const timer = setTimeout(() => {
      const ROW_HEIGHT = 36;
      const scrollTop = targetIndex * ROW_HEIGHT;
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
      onScrollComplete?.();
    }, 100);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToProductId, productInfoList]);

  // Clear selection and reset pagination when tab changes
  useEffect(() => {
    setSelectedProduct(null);
    setVisibleCount(PAGE_SIZE);
  }, [tabPosition]);

  // Reset pagination when search changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchName]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      setVisibleCount((prev) => prev + PAGE_SIZE);
    }
  }, []);

  // Compute filtered + added-flagged list for current tab
  const addedForTab = (addedProductList ?? []).filter(
    (d) => d.priceDetailTypeId === activePart
  );

  const filteredList = productInfoList
    .filter((data) => {
      if (!data.productName) return false;
      if (searchName && !data.productName.includes(searchName)) return false;
      return true;
    })
    .map((data) => ({
      ...data,
      added: addedForTab.some((addedData) => predictFn(data, addedData)),
    }));

  const handleSelect = (data) => {
    setSelectedProduct((prev) => {
      if (
        prev &&
        prev.productId === data.productId &&
        prev.productDetailId === data.productDetailId
      ) {
        return null;
      }
      return data;
    });
  };

  const selectedDisplaySize = selectedProduct
    ? [selectedProduct.rankName, selectedProduct.productClassName, selectedProduct.packingName]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>商品追加</Typography>
          <Chip
            label={`種別：${TABS_LABEL[activePart] || ""}`}
            size="small"
            color="primary"
            sx={{ fontSize: 12, fontWeight: 600 }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="データを再取得">
            <span>
              <IconButton
                size="small"
                onClick={fetchProducts}
                disabled={loading}
                sx={{ color: "text.secondary" }}
              >
                {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={onClose}
            sx={{
              fontSize: 12,
              py: 0.5,
              px: 1.5,
              minWidth: "auto",
              color: "grey.600",
              borderColor: "grey.400",
              "&:hover": {
                color: "error.main",
                borderColor: "error.main",
                bgcolor: "error.50",
              },
            }}
          >
            閉じる
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* 商品追加・商品明細追加 buttons */}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => onAddProductMaster?.()}
          sx={{ fontSize: 12, flex: 1 }}
        >
          商品追加
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!selectedProduct}
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => onAddProductDetailMaster?.({
            productId: selectedProduct.productId,
            productName: selectedProduct.productName,
          })}
          sx={{ fontSize: 12, flex: 1 }}
        >
          商品明細追加
        </Button>
      </Box>

      {/* Search filter */}
      <Box sx={{ mb: 1 }}>
        <OutlinedInput
          size="small"
          fullWidth
          placeholder="商品名で絞り込み..."
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          sx={{ fontSize: 13, "& input": { py: 0.75, px: 1 } }}
        />
      </Box>

      {/* Selected product label */}
      <Box
        sx={{
          mb: 1,
          px: 1,
          py: 0.5,
          bgcolor: "grey.100",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.300",
          minHeight: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {selectedProduct ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
              {`品名：${selectedProduct.productName || ""}　　産地：${selectedProduct.origin || ""}　　規格：${selectedDisplaySize || ""}　　発注単位：${selectedProduct.orderingUnit || ""}`}
            </Typography>
            <IconButton size="small" onClick={() => setSelectedProduct(null)} sx={{ ml: 0.5, p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            選択している商品：なし
          </Typography>
        )}
      </Box>

      {/* Column header */}
      <AddProductSidebarHeader />

      {/* Scrollable product list with infinite scroll */}
      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {loading && productInfoList.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredList.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, fontSize: "0.8rem" }}>
            該当する商品がマスタに存在しません
          </Typography>
        ) : (
          <>
            {filteredList.slice(0, visibleCount).map((data, i) => (
              <AddProductSidebarRow
                key={`${data.productId}-${data.productDetailId}-${i}`}
                data={data}
                part={activePart}
                selected={
                  selectedProduct?.productId === data.productId &&
                  selectedProduct?.productDetailId === data.productDetailId
                }
                onSelect={handleSelect}
                onDragBegin={onDragBegin}
                onDragCancel={onDragCancel}
              />
            ))}
            {visibleCount < filteredList.length && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AddProductSidebar;
